import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaccaratStartRequest, BaccaratStartRoundResponse } from '@/types/baccarat'
import type { RouletteActiveRoundResponse, RouletteSettleRoundResponse, RouletteStartRequest, RouletteStartRoundResponse } from '@/types/roulette'
import type {
	BlackjackActiveRoundResponse,
	BlackjackDoubleResponse,
	BlackjackHitResponse,
	BlackjackInsuranceRequest,
	BlackjackInsuranceResponse,
	BlackjackSplitResponse,
	BlackjackStandResponse,
	BlackjackStartRequest,
	BlackjackStartRoundResponse,
} from '@/types/blackjack'
import type { CrashRoundHistoryRequest, CrashRoundHistoryResponse } from '@/types/crash'
import type { SlideRoundHistoryRequest, SlideRoundHistoryResponse } from '@/types/slide'
import type {
	MineActiveRoundResponse,
	MineAutoBetRequest,
	MineAutoBetResponse,
	MineCashOutResponse,
	MineRevealTileRequest,
	MineRevealTileResponse,
	MineStartRoundRequest,
	MineStartRoundResponse,
} from '@/types/mine'
import type {
	ChickenActiveRoundResponse,
	ChickenAutoBetRequest,
	ChickenAutoBetResponse,
	ChickenCashOutResponse,
	ChickenMultipliersRequest,
	ChickenMultipliersResponse,
	ChickenStartRoundRequest,
	ChickenStartRoundResponse,
	ChickenStepResponse,
} from '@/types/chicken'
import type { OriginalBetHistoryRequest, OriginalBetHistoryResponse, OriginalGamesApiResponse, OriginalGamesRequest, OriginalGameTypesApiResponse } from '@/types/original'
import { playerBaseQuery } from './playerBaseQuery'

export const originalApi = createApi({
	reducerPath: 'originalApi',
	baseQuery: playerBaseQuery,
	tagTypes: ['OriginalGames', 'OriginalGameTypes', 'OriginalBetHistory', 'CrashRoundHistory', 'SlideRoundHistory', 'MineActiveRound', 'ChickenActiveRound', 'ChickenMultipliers', 'BlackjackActiveRound'],
	endpoints: (builder) => ({
		getOriginalGames: builder.query<OriginalGamesApiResponse, OriginalGamesRequest | void>({
			query: (params) => ({
				url: '/original/game',
				...(params ? { params } : {}),
			}),
			providesTags: ['OriginalGames'],
		}),
		getOriginalGameTypes: builder.query<OriginalGameTypesApiResponse, void>({
			query: () => '/original/types',
			providesTags: ['OriginalGameTypes'],
		}),
		getOriginalBetHistory: builder.query<OriginalBetHistoryResponse, OriginalBetHistoryRequest>({
			query: (params) => ({
				url: '/history/original',
				params,
			}),
			providesTags: ['OriginalBetHistory'],
		}),
		getCrashRoundHistory: builder.query<CrashRoundHistoryResponse, CrashRoundHistoryRequest>({
			query: ({ page, per_page }) => ({
				url: '/original/crash/history',
				params: { page, per_page },
			}),
			providesTags: ['CrashRoundHistory'],
		}),
		getSlideRoundHistory: builder.query<SlideRoundHistoryResponse, SlideRoundHistoryRequest>({
			query: ({ page, per_page }) => ({
				url: '/original/slide/history',
				params: { page, per_page },
			}),
			providesTags: ['SlideRoundHistory'],
		}),
		getMineActiveRound: builder.query<MineActiveRoundResponse, void>({
			query: () => '/original/mine/active',
			providesTags: ['MineActiveRound'],
		}),
		startMineRound: builder.mutation<MineStartRoundResponse, MineStartRoundRequest>({
			query: (body) => ({
				url: '/original/mine/start',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['MineActiveRound', 'OriginalBetHistory'],
		}),
		revealMineTile: builder.mutation<MineRevealTileResponse, MineRevealTileRequest>({
			query: (body) => ({
				url: '/original/mine/reveal',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['MineActiveRound'],
		}),
		cashOutMineRound: builder.mutation<MineCashOutResponse, void>({
			query: () => ({
				url: '/original/mine/cashout',
				method: 'POST',
			}),
			invalidatesTags: ['MineActiveRound', 'OriginalBetHistory'],
		}),
		placeMineAutoBet: builder.mutation<MineAutoBetResponse, MineAutoBetRequest>({
			query: (body) => ({
				url: '/original/mine/auto-bet',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['MineActiveRound', 'OriginalBetHistory'],
		}),
		getChickenActiveRound: builder.query<ChickenActiveRoundResponse, void>({
			query: () => '/original/chicken/active',
			providesTags: ['ChickenActiveRound'],
		}),
		getChickenMultipliers: builder.query<ChickenMultipliersResponse, ChickenMultipliersRequest>({
			query: ({ difficulty }) => ({
				url: '/original/chicken/multipliers',
				params: { difficulty },
			}),
			providesTags: ['ChickenMultipliers'],
		}),
		startChickenRound: builder.mutation<ChickenStartRoundResponse, ChickenStartRoundRequest>({
			query: (body) => ({
				url: '/original/chicken/start',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['ChickenActiveRound', 'OriginalBetHistory'],
		}),
		stepChickenRound: builder.mutation<ChickenStepResponse, void>({
			query: () => ({
				url: '/original/chicken/step',
				method: 'POST',
				body: {},
			}),
			invalidatesTags: ['ChickenActiveRound'],
		}),
		cashOutChickenRound: builder.mutation<ChickenCashOutResponse, void>({
			query: () => ({
				url: '/original/chicken/cashout',
				method: 'POST',
			}),
			invalidatesTags: ['ChickenActiveRound', 'OriginalBetHistory'],
		}),
		placeChickenAutoBet: builder.mutation<ChickenAutoBetResponse, ChickenAutoBetRequest>({
			query: (body) => ({
				url: '/original/chicken/auto-bet',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['ChickenActiveRound', 'OriginalBetHistory'],
		}),
		getBlackjackActiveRound: builder.query<BlackjackActiveRoundResponse, void>({
			query: () => '/original/blackjack/active',
			providesTags: ['BlackjackActiveRound'],
		}),
		startBlackjackRound: builder.mutation<BlackjackStartRoundResponse, BlackjackStartRequest>({
			query: (body) => ({
				url: '/original/blackjack/start',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['BlackjackActiveRound', 'OriginalBetHistory'],
		}),
		hitBlackjack: builder.mutation<BlackjackHitResponse, void>({
			query: () => ({
				url: '/original/blackjack/hit',
				method: 'POST',
			}),
		}),
		standBlackjack: builder.mutation<BlackjackStandResponse, void>({
			query: () => ({
				url: '/original/blackjack/stand',
				method: 'POST',
			}),
			invalidatesTags: ['BlackjackActiveRound', 'OriginalBetHistory'],
		}),
		doubleBlackjack: builder.mutation<BlackjackDoubleResponse, void>({
			query: () => ({
				url: '/original/blackjack/double',
				method: 'POST',
			}),
			invalidatesTags: ['BlackjackActiveRound', 'OriginalBetHistory'],
		}),
		splitBlackjack: builder.mutation<BlackjackSplitResponse, void>({
			query: () => ({
				url: '/original/blackjack/split',
				method: 'POST',
			}),
		}),
		insuranceBlackjack: builder.mutation<BlackjackInsuranceResponse, BlackjackInsuranceRequest>({
			query: (body) => ({
				url: '/original/blackjack/insurance',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['BlackjackActiveRound', 'OriginalBetHistory'],
		}),
		startBaccaratRound: builder.mutation<BaccaratStartRoundResponse, BaccaratStartRequest>({
			query: (body) => ({
				url: '/original/baccarat/start',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['OriginalBetHistory'],
		}),
		getRouletteActiveRound: builder.query<RouletteActiveRoundResponse, void>({
			query: () => '/original/roulette/active',
		}),
		startRouletteRound: builder.mutation<RouletteStartRoundResponse, RouletteStartRequest>({
			query: (body) => ({
				url: '/original/roulette/start',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['OriginalBetHistory'],
		}),
		settleRouletteRound: builder.mutation<RouletteSettleRoundResponse, void>({
			query: () => ({
				url: '/original/roulette/settle',
				method: 'POST',
				body: {},
			}),
			invalidatesTags: ['OriginalBetHistory'],
		}),
	}),
})

export const {
	useGetOriginalGamesQuery,
	useGetOriginalGameTypesQuery,
	useGetOriginalBetHistoryQuery,
	useGetCrashRoundHistoryQuery,
	useGetSlideRoundHistoryQuery,
	useGetMineActiveRoundQuery,
	useStartMineRoundMutation,
	useRevealMineTileMutation,
	useCashOutMineRoundMutation,
	usePlaceMineAutoBetMutation,
	useGetChickenActiveRoundQuery,
	useGetChickenMultipliersQuery,
	useLazyGetChickenMultipliersQuery,
	useStartChickenRoundMutation,
	useStepChickenRoundMutation,
	useCashOutChickenRoundMutation,
	usePlaceChickenAutoBetMutation,
	useGetBlackjackActiveRoundQuery,
	useStartBlackjackRoundMutation,
	useHitBlackjackMutation,
	useStandBlackjackMutation,
	useDoubleBlackjackMutation,
	useSplitBlackjackMutation,
	useInsuranceBlackjackMutation,
	useStartBaccaratRoundMutation,
	useGetRouletteActiveRoundQuery,
	useStartRouletteRoundMutation,
	useSettleRouletteRoundMutation,
} = originalApi
